"use strict";
var __knownSymbol = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __using = (stack, value, async) => {
  if (value != null) {
    if (typeof value !== "object" && typeof value !== "function") __typeError("Object expected");
    var dispose, inner;
    if (async) dispose = value[__knownSymbol("asyncDispose")];
    if (dispose === void 0) {
      dispose = value[__knownSymbol("dispose")];
      if (async) inner = dispose;
    }
    if (typeof dispose !== "function") __typeError("Object not disposable");
    if (inner) dispose = function() {
      try {
        inner.call(this);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    stack.push([async, dispose, value]);
  } else if (async) {
    stack.push([async]);
  }
  return value;
};
var __callDispose = (stack, error, hasError) => {
  var E = typeof SuppressedError === "function" ? SuppressedError : function(e, s, m, _) {
    return _ = Error(m), _.name = "SuppressedError", _.error = e, _.suppressed = s, _;
  };
  var fail = (e) => error = hasError ? new E(e, error, "An error was suppressed during disposal") : (hasError = true, e);
  var next = (it) => {
    while (it = stack.pop()) {
      try {
        var result = it[1] && it[1].call(it[2]);
        if (it[0]) return Promise.resolve(result).then(next, (e) => (fail(e), next()));
      } catch (e) {
        fail(e);
      }
    }
    if (hasError) throw error;
  };
  return next();
};
/**
 * @license
 * Copyright 2020 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  CredentialsProvider,
  makeCredentialsGetter
} from "#src/credentials_provider/index.js";
import {
  getCredentialsWithStatus,
  monitorAuthPopupWindow
} from "#src/credentials_provider/interactive_credentials_provider.js";
import { StatusMessage } from "#src/status.js";
import { raceWithAbort } from "#src/util/abort.js";
import {
  verifyObject,
  verifyObjectProperty,
  verifyString,
  verifyStringArray
} from "#src/util/json.js";
import { ProgressSpan } from "#src/util/progress_listener.js";
import { Signal } from "#src/util/signal.js";
function openPopupCenter(url, width, height) {
  const top = window.outerHeight - window.innerHeight + window.innerHeight / 2 - height / 2;
  const left = window.innerWidth / 2 - width / 2;
  return window.open(
    url,
    void 0,
    `toolbar=no, menubar=no, width=${width}, height=${height}, top=${top}, left=${left}`
  );
}
function waitForPopupResponseMessage(source, signal) {
  return new Promise((resolve) => {
    window.addEventListener(
      "message",
      (event) => {
        if (event.source !== source) return;
        resolve(event.data);
      },
      { signal }
    );
  });
}
async function waitForRemoteFlow(url, startMessage, startAction, retryMessage, closedMessage) {
  const status = new StatusMessage(
    /*delay=*/
    false,
    /*modal=*/
    true
  );
  const res = new Promise((f) => {
    function writeStatus(message, buttonMessage) {
      status.element.textContent = message + " ";
      const button = document.createElement("button");
      button.textContent = buttonMessage;
      status.element.appendChild(button);
      button.addEventListener("click", () => {
        writeStatus(retryMessage, "Retry");
        const popup = openPopupCenter(url, 400, 650);
        const closePopup = () => {
          popup?.close();
        };
        window.addEventListener("beforeunload", closePopup);
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            writeStatus(closedMessage, "Retry");
          }
        }, 1e3);
        const messageListener = async (ev) => {
          if (ev.source === popup) {
            clearInterval(checkClosed);
            window.removeEventListener("message", messageListener);
            window.removeEventListener("beforeunload", closePopup);
            closePopup();
            f(ev.data);
          }
        };
        window.addEventListener("message", messageListener);
      });
    }
    writeStatus(startMessage, startAction);
  });
  try {
    return await res;
  } finally {
    status.dispose();
  }
}
async function showTosForm(url, tosName) {
  const data = await waitForRemoteFlow(
    url,
    `Before you can access ${tosName}, you need to accept its Terms of Service.`,
    "Open",
    "Waiting for Terms of Service agreement...",
    `Terms of Service closed for ${tosName}.`
  );
  return data === "success";
}
async function waitForLogin(serverUrl, signal) {
  const abortController = new AbortController();
  signal = AbortSignal.any([abortController.signal, signal]);
  try {
    const newWindow = openPopupCenter(
      `${serverUrl}/api/v1/authorize`,
      400,
      650
    );
    if (newWindow === null) {
      throw new Error("Failed to create authentication popup window");
    }
    monitorAuthPopupWindow(newWindow, abortController);
    return await raceWithAbort(
      waitForPopupResponseMessage(newWindow, abortController.signal).then(
        async (data) => {
          try {
            const obj = verifyObject(data);
            const accessToken = verifyObjectProperty(
              obj,
              "token",
              verifyString
            );
            const appUrls = verifyObjectProperty(
              obj,
              "app_urls",
              verifyStringArray
            );
            return {
              tokenType: "Bearer",
              accessToken,
              url: serverUrl,
              appUrls
            };
          } catch (parseError) {
            throw new Error(
              `Received unexpected authentication response: ${parseError.message}`
            );
          }
        }
      ),
      signal
    );
  } finally {
    abortController.abort();
  }
}
const LOCAL_STORAGE_AUTH_KEY = "auth_token_v2";
function getAuthTokenFromLocalStorage(authURL) {
  const token = localStorage.getItem(`${LOCAL_STORAGE_AUTH_KEY}_${authURL}`);
  if (token) {
    return JSON.parse(token);
  }
  return null;
}
function saveAuthTokenToLocalStorage(authURL, value) {
  localStorage.setItem(
    `${LOCAL_STORAGE_AUTH_KEY}_${authURL}`,
    JSON.stringify(value)
  );
}
const middleAuthLoginEvent = new Event("middleauthlogin");
export class MiddleAuthCredentialsProvider extends CredentialsProvider {
  constructor(serverUrl) {
    super();
    this.serverUrl = serverUrl;
  }
  updated = new Signal();
  cachedGet = this.updateCachedGet();
  get = (invalidCredentials, options) => {
    return this.cachedGet(invalidCredentials, options);
  };
  updateCachedGet() {
    let alreadyTriedLocalStorage = false;
    const res = makeCredentialsGetter(async (options) => {
      var _stack = [];
      try {
        let token = void 0;
        if (!alreadyTriedLocalStorage) {
          alreadyTriedLocalStorage = true;
          token = getAuthTokenFromLocalStorage(this.serverUrl);
          if (token) return token;
        }
        const _span = __using(_stack, new ProgressSpan(options.progressListener, {
          message: `Waiting for middleauth login to ${this.serverUrl}`
        }));
        token = await getCredentialsWithStatus(
          {
            description: `middleauth server ${this.serverUrl}`,
            requestDescription: "login",
            get: (signal) => waitForLogin(this.serverUrl, signal)
          },
          options.signal
        );
        saveAuthTokenToLocalStorage(this.serverUrl, token);
        window.dispatchEvent(middleAuthLoginEvent);
        return token;
      } catch (_) {
        var _error = _, _hasError = true;
      } finally {
        __callDispose(_stack, _error, _hasError);
      }
    });
    this.cachedGet = res;
    this.updated.dispatch();
    return res;
  }
}
export class UnverifiedApp extends Error {
  url;
  constructor(url) {
    super();
    this.url = url;
  }
}
export class MiddleAuthAppCredentialsProvider extends CredentialsProvider {
  constructor(serverUrl, credentialsManager) {
    super();
    this.serverUrl = serverUrl;
    this.credentialsManager = credentialsManager;
  }
  credentials = void 0;
  agreedToTos = false;
  cachedGet = this.updateCachedGet();
  get = (invalidCredentials, options) => {
    return this.cachedGet(invalidCredentials, options);
  };
  updateCachedGet() {
    const res = makeCredentialsGetter(async (options) => {
      var _stack = [];
      try {
        if (this.credentials && this.agreedToTos) {
          return this.credentials.credentials;
        }
        this.agreedToTos = false;
        const { progressListener, signal } = options;
        const _span = __using(_stack, new ProgressSpan(progressListener, {
          message: `Determining authentication server for ${this.serverUrl}`
        }));
        const response = await fetch(`${this.serverUrl}/auth_info`, {
          signal
        });
        const authInfo = await response.json();
        const provider = this.credentialsManager.getCredentialsProvider(
          "middleauth",
          authInfo.login_url
        );
        this.credentials = await provider.get(this.credentials, options);
        const removeHandler = this.registerDisposer(
          provider.updated.add(() => {
            removeHandler();
            this.updateCachedGet();
          })
        );
        if (this.credentials.credentials.appUrls.includes(this.serverUrl)) {
          return this.credentials.credentials;
        }
        const status = new StatusMessage(
          /*delay=*/
          false
        );
        status.setText(`middleauth: unverified app ${this.serverUrl}`);
        throw new UnverifiedApp(this.serverUrl);
      } catch (_) {
        var _error = _, _hasError = true;
      } finally {
        __callDispose(_stack, _error, _hasError);
      }
    });
    this.cachedGet = res;
    return res;
  }
  errorHandler = async (error, credentials) => {
    const { status } = error;
    if (status === 401) {
      return "refresh";
    }
    if (status === 403) {
      const { response } = error;
      if (response) {
        const { headers } = response;
        const contentType = headers.get("content-type");
        if (contentType === "application/json") {
          const json = await response.json();
          if (json.error && json.error === "missing_tos") {
            const url = new URL(json.data.tos_form_url);
            url.searchParams.set("client", "ng");
            const success = await showTosForm(
              url.toString(),
              json.data.tos_name
            );
            if (success) {
              this.agreedToTos = true;
              return "refresh";
            }
          }
        }
      }
      if (!credentials.accessToken) {
        return "refresh";
      }
    }
    throw error;
  };
}
